"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  backfillProgressSummaries,
  hydrateProgressFromServer,
  seedWorkingPieceFromServer,
} from "@/lib/learning-journey/hydrate-progress";
import { repertoire } from "@/lib/learning-journey/repertoire";
import {
  getToken,
  setToken,
  clearToken,
  getStoredUser,
  setStoredUser,
  isTokenExpired,
  type VirtuosaUser,
} from "@/lib/api/auth";
import {
  openSession,
  register as apiRegister,
  claimLibrary,
  fetchMe,
} from "@/lib/api/api-client";
import {
  clearPendingClaims,
  readPendingClaims,
} from "@/lib/library/pending-claims";
import { resetAnalyticsSession, track } from "@/lib/analytics";

interface AuthContextValue {
  user: VirtuosaUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Ouvre une session sans avoir demande si la personne est nouvelle.
   *
   * Rend `"unknown-email"` quand aucun compte ne porte cette adresse : ce
   * n est pas une erreur, c est le chemin de creation, et c est a l ecran
   * de compte de proposer la confirmation.
   */
  signIn: (email: string, password: string) => Promise<"signed-in" | "unknown-email">;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  /**
   * Nombre de morceaux que la dernière revendication a mis dans la liste,
   * `null` quand il n'y a rien à annoncer. C'est le seul moment où la promesse
   * du compte devient visible — sans ce retour, « Mes morceaux » reste un
   * onglet que personne n'ouvre.
   */
  claimedEntries: number | null;
  /** Consommé par le toast une fois affiché. */
  acknowledgeClaim: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<VirtuosaUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [claimedEntries, setClaimedEntries] = useState<number | null>(null);
  /*
    Instant d'ouverture de la session en cours, pour en donner la durée à la
    déconnexion.

    Posé au montage puis redéposé à chaque ouverture de session : quelqu'un qui
    arrive en anonyme, joue vingt minutes, se connecte puis se déconnecte n'a
    pas tenu vingt-cinq minutes de session connectée, il en a tenu cinq.

    Quand le jeton est simplement retrouvé au chargement, la connexion peut
    dater de la semaine dernière et rien ici ne le sait : c'est alors l'arrivée
    sur la page qui sert de repère, ce qui est aussi la borne de la session
    analytique que la déconnexion referme.

    Posé par un effet et non à l'initialisation du ref : `Date.now()` dans le
    corps du composant est un appel impur au rendu, que le linter refuse et
    qu'un rendu rejoué ne redonnerait pas à l'identique.
  */
  const sessionStartedAtRef = useRef(0);

  useEffect(() => {
    sessionStartedAtRef.current = Date.now();
  }, []);

  /**
   * Revendique les imports faits sans compte.
   *
   * Jamais attendu par l'appelant : la modale se ferme sur la pose du jeton,
   * pas sur cette requête. Le hachage du mot de passe coûte déjà plusieurs
   * centaines de millisecondes, on n'enchaîne pas un second aller-retour
   * bloquant avant de rendre la main.
   *
   * En cas d'échec on ne vide rien : `POST /api/library/claim` est idempotent,
   * et la tentative se rejouera au montage suivant.
   */
  const claimPendingImports = useCallback(async () => {
    const pending = readPendingClaims();

    if (pending.length === 0) {
      return;
    }

    const ids = pending.map((entry) => entry.midiFileId);

    try {
      const result = await claimLibrary(ids);
      clearPendingClaims(ids);

      track({
        name: "library_claim_completed",
        properties: {
          requested: result.requested,
          entries: result.entries,
          attached: result.attached,
        },
      });

      if (result.entries > 0) {
        setClaimedEntries(result.entries);
      }
    } catch {
      // Registre conservé : on retentera.
    }
  }, []);

  /**
   * Redescend la progression du compte. Posée au même endroit que la
   * revendication des imports : c'est le moment où la session est confirmée,
   * donc le seul où l'on sait à qui appartient ce qu'on va chercher.
   *
   * Ne remplit que ce qui manque localement — voir `hydrate-progress.ts`.
   */
  const hydrateProgress = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    await hydrateProgressFromServer(window.localStorage);
    // Dans cet ordre : c'est la présence d'un parcours redescendu qui
    // distingue un morceau travaillé d'un morceau seulement ouvert.
    await seedWorkingPieceFromServer(window.localStorage, repertoire());

    // Non attendu : le rattrapage fait une écriture par morceau et ne sert
    // qu'à un écran qu'on n'est pas en train d'ouvrir. Le faire attendre
    // retarderait l'entrée dans l'application pour un bénéfice différé, et il
    // ne tourne de toute façon qu'une fois par navigateur.
    void backfillProgressSummaries(window.localStorage);
  }, []);

  const acknowledgeClaim = useCallback(() => {
    setClaimedEntries(null);
  }, []);

  // Check existing token on mount
  useEffect(() => {
    const token = getToken();

    if (token && !isTokenExpired(token)) {
      const stored = getStoredUser();

      if (stored) {
        setUser(stored);
      }

      // Refresh user data in background
      fetchMe()
        .then((u) => {
          setUser(u);
          setStoredUser(u);
          // Rattrape une revendication qui aurait échoué la fois d'avant, ou
          // des imports faits en anonyme dans un autre onglet.
          void claimPendingImports();
          void hydrateProgress();
        })
        .catch(() => {
          // Token invalid
          clearToken();
          setUser(null);
        })
        .finally(() => setIsLoading(false));
    } else {
      if (token) {
        clearToken();
      }

      setIsLoading(false);
    }
    // `claimPendingImports` est stable (useCallback sans dépendance) : le
    // montage reste unique.
  }, [claimPendingImports, hydrateProgress]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<"signed-in" | "unknown-email"> => {
      const result = await openSession(email, password);

      if ("known" in result) {
        return "unknown-email";
      }

      setToken(result.token);
      setStoredUser(result.user);
      setUser(result.user);
      sessionStartedAtRef.current = Date.now();

      // À la connexion aussi, pas seulement à l'inscription : quelqu'un qui
      // importe en anonyme puis se connecte à un compte existant doit
      // récupérer ses imports.
      void claimPendingImports();

      return "signed-in";
    },
    [claimPendingImports],
  );

  const register = useCallback(
    async (email: string, password: string) => {
      const result = await apiRegister(email, password);
      setToken(result.token);
      setStoredUser(result.user);
      setUser(result.user);
      sessionStartedAtRef.current = Date.now();

      void claimPendingImports();
      void hydrateProgress();
    },
    [claimPendingImports, hydrateProgress],
  );

  const logout = useCallback(() => {
    // Avant `resetAnalyticsSession()`, impérativement : après, l'événement
    // serait mis en file sous la session anonyme qui vient d'être ouverte, donc
    // porté au crédit de la personne suivante. La déconnexion ne laissait
    // aucune trace, alors qu'elle est le seul geste qui distingue un compte qui
    // s'en va d'un compte qui s'arrête d'un coup.
    track({
      name: "account_logged_out",
      properties: { sessionDurationMs: Date.now() - sessionStartedAtRef.current },
    });

    // Avant `clearToken()`, à double titre : la file en attente part sous
    // l'identité de la session qui s'achève, et tant que le jeton permet
    // encore de rattacher ces événements au compte qui les a produits.
    //
    // Sans ce reset, la session anonyme survivrait au changement de compte —
    // `logout()` ne recharge pas la page. Sur un poste partagé, les deux
    // comptes finiraient fusionnés en une seule personne côté analytique.
    resetAnalyticsSession();

    clearToken();
    setUser(null);
    setClaimedEntries(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await fetchMe();
    setUser(u);
    setStoredUser(u);
  }, []);

  // Mémoïsée : un objet neuf à chaque rendu du fournisseur re-rendait tous
  // les consommateurs d'`useAuth`, jusqu'au bas de l'arbre.
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      signIn,
      register,
      logout,
      refreshUser,
      claimedEntries,
      acknowledgeClaim,
    }),
    [user, isLoading, signIn, register, logout, refreshUser, claimedEntries, acknowledgeClaim],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }

  return context;
}
